import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import TicketListModal from "../../core/modals/ticketListModal";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import { AppDispatch } from "../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
import { fetchTicketCategorySummary, fetchTicketCategorySummaryByDepartment } from "../../core/data/redux/tickets/ticketCategoryActions";
import { fetchAllTickets, fetchTicketsByDepartment, fetchTicketsByEmployeeHierarchy, fetchTicketsSummaryByDepartment, fetchTicketSummary } from "../../core/data/redux/tickets/ticket/ticketActions";
import TicketCard from "./TicketDashboardCard";
import { exportToCSV, exportToExcel, exportToPDF } from "../../core/common/GlobalFunctions/exportTableData";
import { decryptData } from "../../core/utils/encryptUtils";

// --- Constants ---
const priorityMap: Record<string, string> = {
  High: "0",
  Medium: "1",
  Low: "2",
};

const getPriorityLabel = (priority: string | number) => {
  switch (Number(priority)) {
    case 0: return "High";
    case 1: return "Medium";
    case 2: return "Low";
    default: return "All";
  }
};

function collectTicketsFromHierarchy(data: any): any[] {
  if (!data) return [];

  const bag: any[] = [];

  // handle "tree" (could be a single node or an array when includeSelf=false)
  const walk = (node: any) => {
    if (!node) return;

    // ✅ Collect both assigned and created tickets
    if (Array.isArray(node.assignedTickets)) bag.push(...node.assignedTickets);
    if (Array.isArray(node.createdTickets)) bag.push(...node.createdTickets);

    if (Array.isArray(node.children)) node.children.forEach(walk);
  };

  if (Array.isArray(data.tree)) data.tree.forEach(walk);
  else walk(data.tree);

  // ✅ include unassigned group (if present)
  if (Array.isArray(data?.unassignedGroup?.tickets)) {
    bag.push(...data.unassignedGroup.tickets);
  }

  // ✅ support flat format
  if (Array.isArray(data?.groups)) {
    for (const g of data.groups) {
      if (Array.isArray(g?.tickets)) bag.push(...g.tickets);
    }
  }

  // ✅ de-dupe by ticket_id (last one wins)
  const map = new Map<number, any>();
  for (const t of bag) map.set(Number(t.ticket_id), t);

  // ✅ sort newest first
  const out = Array.from(map.values()).sort((a, b) => {
    const ta = a.ModifiedAt ? new Date(a.ModifiedAt).getTime() : 0;
    const tb = b.ModifiedAt ? new Date(b.ModifiedAt).getTime() : 0;
    return tb - ta;
  });

  return out;
}

type TicketRow = {
  ticket_id: number | string;
  ticket_status?: string | null;
  ticket_category_name?: string | null;
  DepartmentId?: number | null;
  ModifiedAt?: string;
  AssignedTo?: number | null;
  assigned_to_name?: string | null;
};

type StatusSummary = {
  open_tickets: number;
  pending_tickets: number;
  solved_tickets: number;
  discussion_tickets: number;
  development_tickets: number;
  fixed_tickets: number;
  testing_tickets: number;
  closed_tickets: number;
  unassigned_tickets: number;
  department_id: number | null;
  assigned_to: null;
};

type CategoryBucket = { category_name: string; ticket_count: number };

function computeHierarchySummaries(
  allHierarchyTickets: TicketRow[] | undefined,
  opts?: { departmentId?: number | null }
): { statusSummary: StatusSummary; categorySummary: CategoryBucket[] } {
  const input = Array.isArray(allHierarchyTickets) ? allHierarchyTickets : [];

  // --- Dedupe by ticket_id (keep the most recent ModifiedAt if present) ---
  const byId = new Map<string | number, TicketRow>();
  for (const t of input) {
    const id = t.ticket_id;
    if (!byId.has(id)) {
      byId.set(id, t);
    } else {
      const prev = byId.get(id)!;
      const prevTs = prev.ModifiedAt ? new Date(prev.ModifiedAt).getTime() : -Infinity;
      const curTs = t.ModifiedAt ? new Date(t.ModifiedAt).getTime() : -Infinity;
      if (curTs >= prevTs) byId.set(id, t);
    }
  }
  const rows: any = Array.from(byId.values());

  // --- Status summary counts ---
  const summary: StatusSummary = {
    open_tickets: 0,
    pending_tickets: 0,
    solved_tickets: 0,
    discussion_tickets: 0,
    development_tickets: 0,
    fixed_tickets: 0,
    testing_tickets: 0,
    closed_tickets: 0,
    unassigned_tickets: 0,
    department_id: null,
    assigned_to: null,
  };

  const statusKeyMap: Record<string, keyof StatusSummary> = {
    open: "open_tickets",
    pending: "pending_tickets",
    solved: "solved_tickets",
    discussion: "discussion_tickets",
    development: "development_tickets",
    fixed: "fixed_tickets",
    testing: "testing_tickets",
    closed: "closed_tickets",
  };

  // Count department frequency to infer department_id if not supplied
  const deptFreq = new Map<number, number>();

  for (const r of rows) {
    const s = (r.ticket_status || "").toString().trim().toLowerCase();
    const key = statusKeyMap[s];
    if (key) summary[key]++;

    // Unassigned detection: AssignedTo null/undefined OR assigned_to_name "Unassigned"
    const assignedId = r.AssignedTo ?? r.assigned_to_id ?? null;
    const isUnassigned =
      assignedId == null ||
      (typeof r.assigned_to_name === "string" &&
        r.assigned_to_name.trim().toLowerCase() === "unassigned");

    if (isUnassigned) summary.unassigned_tickets++;

    if (typeof r.DepartmentId === "number") {
      deptFreq.set(r.DepartmentId, (deptFreq.get(r.DepartmentId) ?? 0) + 1);
    }
  }

  // Choose department_id: prefer provided option, else most common in data
  if (opts?.departmentId != null) {
    summary.department_id = opts.departmentId as number;
  } else if (deptFreq.size > 0) {
    summary.department_id = Array.from(deptFreq.entries()).sort((a, b) => b[1] - a[1])[0][0];
  } else {
    summary.department_id = null;
  }

  // --- Category summary ---
  const catMap = new Map<string, number>();
  for (const r of rows) {
    const name = (r.ticket_category_name || "").toString().trim();
    if (!name) continue;
    catMap.set(name, (catMap.get(name) ?? 0) + 1);
  }
  const categorySummary: CategoryBucket[] = Array.from(catMap.entries())
    .map(([category_name, ticket_count]) => ({ category_name, ticket_count }))
    .sort((a, b) => b.ticket_count - a.ticket_count);

  return { statusSummary: summary, categorySummary };
}


const Tickets = () => {
  const routes = all_routes;
  const dispatch = useDispatch<AppDispatch>();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { TicketCategorySummry, TicketCategorySummryByDepartment } = useSelector((state: any) => state.ticket.ticketCategory);
  const { TicketSummry, ticketsByDepartment, ticketsSummaryByDepartment, tickets, ticketsByEmployeeHierarchy } = useSelector((state: any) => state.ticket.createTickit);
  const { user } = useSelector((state: any) => state.auth);
  const { employeesbyUser } = useSelector((state: any) => state.employee);
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);

  // Show modal
  const showModal = () => setIsModalVisible(true);
  const handleCancel = () => setIsModalVisible(false);

  const allHierarchyTickets = useMemo(
    () => collectTicketsFromHierarchy(ticketsByEmployeeHierarchy),
    [ticketsByEmployeeHierarchy]
  );

  const { statusSummary, categorySummary } = useMemo(
    () => computeHierarchySummaries(allHierarchyTickets, { departmentId: employeesbyUser?.DepartmentId }),
    [allHierarchyTickets, employeesbyUser?.DepartmentId]
  );

  //permission
  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

  const canAdd = permissions.includes("Add") || permissions.includes("AllowAll");
  const canExport = permissions.includes("Export") || permissions.includes("AllowAll");

  // Fetch data
  useEffect(() => {
    if (!TicketCategorySummry?.length) dispatch(fetchTicketCategorySummary());
    if (!TicketSummry?.assigned_to) dispatch(fetchTicketSummary());
    if (!tickets.length) dispatch(fetchAllTickets());

    if (employeesbyUser?.DepartmentId) {
      dispatch(fetchTicketsByDepartment(employeesbyUser.DepartmentId));
      dispatch(fetchTicketsSummaryByDepartment(employeesbyUser.DepartmentId));
      dispatch(fetchTicketCategorySummaryByDepartment(employeesbyUser.DepartmentId));
    }
  }, [dispatch, TicketCategorySummry?.length, TicketSummry?.assigned_to, tickets.length, employeesbyUser?.DepartmentId]);

  useEffect(() => {
    if (employeesbyUser?.employeeId && employeesbyUser?.DepartmentId) {
      dispatch(fetchTicketsByEmployeeHierarchy({
        employeeId: employeesbyUser.employeeId,
        departmentId: employeesbyUser.DepartmentId
      }));
    }
  }, [dispatch, employeesbyUser]);

  // Determine data based on role
  const ticketCategorySummaryFilter = user?.role_name === "Admin" ? TicketCategorySummry : categorySummary;
  const ticketsSummaryFilter = user?.role_name === "Admin" ? TicketSummry : statusSummary;
  const ticketsToFilter = user?.role_name === "Admin" ? tickets : allHierarchyTickets;

  // Filter tickets
  const filteredTickets = useMemo(() => {
    return ticketsToFilter.filter((ticket: any) => {
      const matchesPriority = selectedPriority ? ticket.priority === selectedPriority : true;
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = searchQuery
        ? ticket.title?.toLowerCase().includes(searchLower) ||
        ticket.ticket_id?.toString().toLowerCase().includes(searchLower) ||
        ticket.department_name?.toLowerCase().includes(searchLower) ||
        ticket.ticket_status?.toLowerCase().includes(searchLower) ||
        getPriorityLabel(ticket.priority).toLowerCase().includes(searchLower)
        : true;

      const matchesUnassigned = showUnassignedOnly
        ? (!ticket.assigned_to_name || ticket.assigned_to_name === 'Unassigned')
        : true;

      return matchesPriority && matchesSearch && matchesUnassigned;
    });
  }, [ticketsToFilter, selectedPriority, searchQuery, showUnassignedOnly]);

  // Export minimal data
  const exportData = useCallback((type: "csv" | "excel" | "pdf") => {
    const minimalData = filteredTickets.map((ticket: any) => ({
      Ticket_Number: ticket.ticket_number,
      Title: ticket.title,
      Description: ticket.description,
      Status: ticket.ticket_status,
      Priority: getPriorityLabel(ticket.priority),
    }));
    if (type === "csv") {
      exportToCSV(minimalData, "tickets_export");
    } else if (type === "excel") {
      exportToExcel(minimalData, "tickets_export");
    } else if (type === "pdf") {
      exportToPDF(minimalData, "tickets_export");
    }

  }, [filteredTickets]);


  // Ticket categories UI
  const ticketCategories = [
    { category: 'Open Tickets', ticketCount: ticketsSummaryFilter?.open_tickets, badgeColor: 'border-primary', icon: 'ti ti-folder' },
    { category: 'Pending Tickets', ticketCount: ticketsSummaryFilter?.pending_tickets, badgeColor: 'border-info', icon: 'ti ti-progress-alert' },
    { category: 'Solved Tickets', ticketCount: ticketsSummaryFilter?.solved_tickets, badgeColor: 'border-success', icon: 'ti ti-checks' },
    { category: 'Discussion Tickets', ticketCount: ticketsSummaryFilter?.discussion_tickets, badgeColor: 'border-warning', icon: 'ti ti-message-dots' },
    { category: 'Development Tickets', ticketCount: ticketsSummaryFilter?.development_tickets, badgeColor: 'border-secondary', icon: 'ti ti-code' },
    { category: 'Fixed Tickets', ticketCount: ticketsSummaryFilter?.fixed_tickets, badgeColor: 'border-danger', icon: 'ti ti-bug-off' },
    { category: 'Testing Tickets', ticketCount: ticketsSummaryFilter?.testing_tickets, badgeColor: 'border-dark', icon: 'ti ti-flask' },
    { category: 'Close Tickets', ticketCount: ticketsSummaryFilter?.closed_tickets, badgeColor: 'border-dark', icon: 'ti ti-square-x' },
    { category: 'Unassigned Tickets', ticketCount: ticketsSummaryFilter?.unassigned_tickets, badgeColor: 'border-dark', icon: 'ti ti-user-off' },
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Tickets</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}><i className="ti ti-smart-home" /></Link>
                  </li>
                  <li className="breadcrumb-item">Tickets Management</li>
                  <li className="breadcrumb-item active">Tickets</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              {/* Export Dropdown */}
              {canExport &&
                <div className="dropdown me-2 mb-2">
                  <Link to="#" className="dropdown-toggle btn btn-white" data-bs-toggle="dropdown">
                    <i className="ti ti-file-export me-1" /> Export
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li><button onClick={() => exportData("csv")} className="dropdown-item"><i className="ti ti-file-type-pdf me-1" /> Export as CSV</button></li>
                    <li><button onClick={() => exportData("excel")} className="dropdown-item"><i className="ti ti-file-type-xls me-1" /> Export as Excel</button></li>
                    <li><button onClick={() => exportData("pdf")} className="dropdown-item"><i className="ti ti-file-type-xls me-1" /> Export as PDF</button></li>
                  </ul>
                </div>
              }
              {/* Add Ticket */}
              {canAdd &&
                <div className="mb-2">
                  <button onClick={showModal} className="btn btn-primary d-flex align-items-center">
                    <i className="ti ti-circle-plus me-2" /> Add Ticket
                  </button>
                </div>
              }
              <div className="head-icons ms-2"><CollapseHeader /></div>
            </div>
          </div>

          {/* Ticket Category Cards */}
          <div className="row">
            {ticketCategories.map((ticket, idx) => (
              <TicketCard key={idx} {...ticket} />
            ))}
          </div>

          {/* Ticket List */}
          <div className="card">
            <div className="card-body p-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap">
                <h5 className="mb-0">Ticket List</h5>
                <div className="d-flex align-items-center gap-2 flex-nowrap mt-2 mt-md-0">
                  <button
                    className={`btn btn-sm ${showUnassignedOnly ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={{ width: '100px' }}
                    onClick={() => setShowUnassignedOnly((prev) => !prev)}
                  >
                    {showUnassignedOnly ? 'Show All' : 'Unassigned'}
                  </button>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search tickets..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ maxWidth: '250px' }}
                  />

                  {/* Priority Dropdown */}
                  <div className="dropdown">
                    <Link to="#" className="dropdown-toggle btn btn-sm btn-white" data-bs-toggle="dropdown">
                      {selectedPriority !== null ? getPriorityLabel(selectedPriority) : "All"}
                    </Link>
                    <ul className="dropdown-menu p-3">
                      {["All", "High", "Medium", "Low"].map((label) => (
                        <li key={label}>
                          <Link
                            to="#"
                            className="dropdown-item"
                            onClick={() => setSelectedPriority(label === "All" ? null : priorityMap[label])}
                          >
                            {label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Ticket Items */}
          <div className="row">
            <div className="col-xl-9 col-md-8">
              {filteredTickets.map((ticket: any) => (
                <div className="card" key={ticket.id}>
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <div className="d-flex">
                      <h5 className="text-info fw-medium me-2 mb-0">{ticket.department_name}</h5>
                      <span className="badge bg-primary text-white">{ticket.ticket_status}</span>
                    </div>
                    <span className={`badge ${Number(ticket.priority) === 0 ? 'bg-danger' : Number(ticket.priority) === 1 ? 'bg-warning' : 'bg-success'} text-white`}>
                      {getPriorityLabel(ticket.priority)}
                    </span>
                  </div>
                  <div className="card-body">
                    <div className="d-flex align-items-center flex-wrap">
                      <p className="d-flex align-items-center mb-0 me-2">
                        <span className="avatar avatar-xl avatar-rounded me-2">
                          <img src={ticket?.assigned_to_img || "https://cdn-icons-png.flaticon.com/512/149/149071.png"} alt="Profile" className="img-fluid rounded-circle" />
                        </span>
                        <div className="d-flex flex-column">
                          <div className="d-flex gap-2">
                            <h5 className="fw-semibold">
                              <Link to={routes.ticketDetails.replace(':ticket_id', ticket.ticket_id)}>{ticket.title}</Link>
                            </h5>
                            <span className="badge badge-secondary rounded-pill mb-2">{ticket.ticket_number}</span>
                          </div>
                          <p className="d-flex">
                            {ticket.assigned_to_name && ticket.assigned_to_name !== 'Unassigned' ? (
                              <div className="d-flex align-items-center me-3 mb-2">
                                <span className="me-2 fw-semibold">Assigned to</span>
                                <span className="text-dark fw-medium">{ticket.assigned_to_name}</span>
                              </div>
                            ) : (
                              <div className="text-danger fw-bold me-3 mb-2">Unassigned</div>
                            )}
                            <p className="mb-0"><i className="ti ti-calendar-bolt " /> Updated {new Date(ticket.ModifiedAt).toLocaleString()}</p>
                          </p>
                        </div>
                      </p>
                    </div>

                  </div>
                </div>
              ))}
            </div>

            {/* Ticket Categories Sidebar */}
            <div className="col-xl-3 col-md-4">
              <div className="card">
                <div className="card-header"><h4>Ticket Categories</h4></div>
                <div className="card-body p-0">
                  {ticketCategorySummaryFilter?.map((category: any) => (
                    <div className="d-flex justify-content-between border-bottom p-3" key={category.category_name}>
                      <Link to="#">{category.category_name}</Link>
                      <span className="badge badge-xs bg-dark rounded-circle">{category.ticket_count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>Developed By <Link to="#" className="text-primary">Leadchain Systems</Link></p>
        </div>
      </div>

      {/* Ticket Modal */}
      <TicketListModal handleCancel={handleCancel} isModalVisible={isModalVisible} />
    </>
  );
};

export default Tickets;
